import { Box } from '@mui/material';
import { atom, useSetAtom } from 'jotai/index';
import { Moment } from 'moment-timezone';
import { useEffect } from 'react';

import { useCompetingVisitsOnDate } from '../../../fetching/api/availability';
import { Modality } from '../../../fetching/sharedSchemas/visits';
import { AxleSuspense } from '../../../fetching/suspense';
import { useConfigValue } from '../../../utils/useConfigValue';
import { GoogleMapStopsProp } from '../../mapping/GoogleMap/GoogleMap';
import { GoogleMapWithAtom } from '../../mapping/GoogleMap/GoogleMapWithAtom';

const stopsAtom = atom<GoogleMapStopsProp>([]);

const CompetingVisitsMapInner = ({
    patientId,
    addressId,
    serviceAreaId,
    serviceBundleId,
    serviceIds,
    date,
    timezone,
}: {
    patientId: string;
    addressId: string;
    serviceAreaId: string;
    serviceBundleId: string;
    serviceIds: string[];
    date: Moment;
    timezone: string;
}) => {
    const { visits_competing_for_attention, tentative_visit_coordinates } =
        useCompetingVisitsOnDate({
            patientId,
            addressId,
            serviceAreaId,
            serviceBundleId,
            serviceIds,
            date,
            timezone,
        });

    const setStops = useSetAtom(stopsAtom);

    useEffect(() => {
        if (visits_competing_for_attention == null) {
            return;
        }
        setStops(
            visits_competing_for_attention
                .filter((visit) => {
                    if (
                        visit.visit_positions.some(
                            (position) => position.modality !== Modality.Virtual,
                        )
                    ) {
                        return true;
                    }
                    return false;
                })
                .map((visit) => ({
                    lat: visit.address_dict.lat,
                    lng: visit.address_dict.lng,
                    group_id: 'competing',
                    datetime: null,
                }))
                .concat([
                    {
                        lat: tentative_visit_coordinates.lat,
                        lng: tentative_visit_coordinates.lng,
                        group_id: 'tentative',
                        datetime: null,
                    },
                ]),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visits_competing_for_attention, tentative_visit_coordinates]);

    if (visits_competing_for_attention === null) {
        return null;
    }

    return (
        <Box
            sx={{
                height: '100%',
                width: '100%',
            }}
        >
            <GoogleMapWithAtom stopsAtom={stopsAtom} drawRoutes={false} />
        </Box>
    );
};
export const CompetingVisitsMap = ({
    patientId,
    addressId,
    serviceAreaId,
    serviceBundleId,
    serviceIds,
    date,
    timezone,
}: {
    patientId: string;
    addressId: string;
    serviceAreaId: string;
    serviceBundleId: string;
    serviceIds: string[];
    date: Moment;
    timezone: string;
}) => {
    // If the org has metrics-by-date disabled, we don't want to run the (expensive)
    //  competing-visits availability query at all, and the map has nothing to show
    //  without it, so render nothing.
    // Note: this gate must live here in the parent, not in the inner component,
    //  because the inner component's query uses suspense, and a suspense query
    //  that is merely disabled (`enabled: false`) never resolves, leaving the
    //  component tree stuck in a suspended state (and `useAxleQuery` throws).
    const showMetricsByDate = useConfigValue('should_display_metrics_by_date_in_booking');
    if (!showMetricsByDate) {
        return null;
    }

    return (
        <AxleSuspense skeletonHeight={'100%'}>
            <CompetingVisitsMapInner
                patientId={patientId}
                addressId={addressId}
                serviceAreaId={serviceAreaId}
                serviceBundleId={serviceBundleId}
                serviceIds={serviceIds}
                date={date}
                timezone={timezone}
            />
        </AxleSuspense>
    );
};
